import { useCallback } from 'react'
import { create } from '@bufbuild/protobuf'
import {
    type ChunkedMedia,
    ChunkedMediaSchema,
    CreationCookie,
    CreationCookieSchema,
    type PlainMessage,
} from '@towns-protocol/proto'
import {
    Attachment,
    ChunkedMediaAttachment,
    MediaInfo,
    streamIdAsString,
} from '@towns-protocol/sdk'
import { refreshSpaceCache, refreshUserImageCache, waitForInvalidation } from 'api/lib/fetchImage'
import imageCompression from 'browser-image-compression'
import { chunkSize, encryptChunkedAESGCM, retryOperation, useTownsClient } from 'use-towns-client'
import { isImageMimeType } from 'utils/isMediaMimeType'

const MAX_THUMBNAIL_WIDTH = 20 // pixels
const MAX_THUMBNAIL_SIZE = 0.0003 // 300 bytes

export const useUploadAttachment = () => {
    const { createMediaStream, setUserProfileImage, sendMediaPayload, setSpaceImage } =
        useTownsClient()

    function shouldCompressFile(file: File): boolean {
        return file.type !== 'image/gif' && isImageMimeType(file.type)
    }

    const createChunkedAttachment = useCallback(
        async (
            data: Uint8Array,
            width: number,
            height: number,
            file: File,
            channelId: string | undefined,
            spaceId: string | undefined,
            userId: string | undefined,
            thumbnail: File | undefined,
            setProgress: (progress: number) => void,
            customCreateMediaStream?: typeof createMediaStream,
            customSendMediaPayload?: typeof sendMediaPayload,
        ): Promise<Attachment> => {
            const createMediaStreamFn = customCreateMediaStream || createMediaStream
            const sendMediaPayloadFn = customSendMediaPayload || sendMediaPayload

            const encryptionResult = await encryptChunkedAESGCM(data, chunkSize)
            const chunkCount = encryptionResult.chunks.length
            if (chunkCount === 0) {
                throw new Error('No media chunks')
            }
            const mediaStreamInfo = await createMediaStreamFn(
                channelId,
                spaceId,
                userId,
                chunkCount,
                encryptionResult.chunks[0].ciphertext,
                encryptionResult.chunks[0].iv,
                true,
            )
            if (!mediaStreamInfo) {
                throw new Error('Failed to create media stream')
            }

            console.log('createChunkedAttachment', {
                spaceId: spaceId ?? 'undefined',
                channelId: channelId ?? 'undefined',
                userId: userId ?? 'undefined',
                creationCookie: mediaStreamInfo.creationCookie ?? 'undefined',
            })

            if (chunkCount > 1) {
                let cc: CreationCookie = create(
                    CreationCookieSchema,
                    mediaStreamInfo.creationCookie,
                )
                for (let chunkIndex = 1; chunkIndex < chunkCount; chunkIndex++) {
                    const chunk = encryptionResult.chunks[chunkIndex]
                    setProgress(chunkIndex / chunkCount)

                    let result = null
                    let retryCount = 0
                    const maxRetries = 3

                    while (retryCount <= maxRetries) {
                        try {
                            result = await sendMediaPayloadFn(
                                cc,
                                chunkIndex == chunkCount - 1,
                                chunk.ciphertext,
                                chunkIndex,
                                chunk.iv,
                            )

                            if (result) {
                                break // Success, exit retry loop
                            }

                            throw new Error('No result returned from sendMediaPayload')
                        } catch (error) {
                            if (retryCount === maxRetries) {
                                console.error(
                                    `Failed to send media payload after ${maxRetries} attempts:`,
                                    error,
                                )
                                throw new Error('Failed to send media payload')
                            }

                            const delay = Math.min(1000 * Math.pow(2, retryCount), 8000) // Initial 1s, max 8s
                            console.warn(
                                `Media payload upload failed. Retry attempt ${
                                    retryCount + 1
                                } after ${delay}ms:`,
                                error,
                            )

                            await new Promise((resolve) => setTimeout(resolve, delay))
                            retryCount++
                        }
                    }

                    if (!result) {
                        throw new Error('Failed to send media payload: No result after retries')
                    }
                    cc = create(CreationCookieSchema, result.creationCookie)
                }
            }
            setProgress(1)

            let thumbnailInfo: { content: Uint8Array; info: MediaInfo } | undefined
            if (thumbnail) {
                thumbnailInfo = {
                    info: {
                        filename: thumbnail.name,
                        mimetype: thumbnail.type,
                        widthPixels: width,
                        heightPixels: height,
                        sizeBytes: BigInt(thumbnail.size),
                    },
                    content: new Uint8Array(await thumbnail.arrayBuffer()),
                }
            }

            return {
                id: streamIdAsString(mediaStreamInfo.creationCookie.streamId),
                type: 'chunked_media',
                streamId: streamIdAsString(mediaStreamInfo.creationCookie.streamId),
                encryption: {
                    iv: new Uint8Array(0),
                    secretKey: encryptionResult.secretKey,
                },
                info: {
                    filename: file.name,
                    mimetype: file.type,
                    widthPixels: width,
                    heightPixels: height,
                    sizeBytes: BigInt(data.length),
                },
                thumbnail: thumbnailInfo,
            } satisfies ChunkedMediaAttachment
        },
        [createMediaStream, sendMediaPayload],
    )

    const uploadFile = useCallback(
        async (
            channelId: string | undefined,
            spaceId: string | undefined,
            file: File,
            setProgress: (progress: number) => void,
        ) => {
            const buffer = await file.arrayBuffer()
            const bytes = new Uint8Array(buffer)
            return await createChunkedAttachment(
                bytes,
                0,
                0,
                file,
                channelId,
                spaceId,
                undefined,
                undefined,
                setProgress,
            )
        },
        [createChunkedAttachment],
    )

    const uploadImageFile = useCallback(
        async (
            channelId: string | undefined,
            spaceId: string | undefined,
            userId: string | undefined,
            file: File,
            setProgress: (progress: number) => void,
            customCreateMediaStream?: typeof createMediaStream,
            customSendMediaPayload?: typeof sendMediaPayload,
        ) => {
            /** Do some basic compression to avoid ridiculous file sizes
             * unless the image is animated
             */
            const compressed = shouldCompressFile(file)
                ? await imageCompression(file, {
                      maxSizeMB: 1,
                      maxWidthOrHeight: 2048,
                  })
                : file

            let thumbnail: File | undefined
            try {
                thumbnail = shouldCompressFile(file)
                    ? await imageCompression(file, {
                          maxSizeMB: MAX_THUMBNAIL_SIZE,
                          maxWidthOrHeight: MAX_THUMBNAIL_WIDTH,
                          alwaysKeepResolution: true,
                      })
                    : undefined
            } catch (e) {
                console.error('Error compressing thumbnail', e)
            }

            const { width, height } = await imageSize(compressed)
            const buffer = await compressed.arrayBuffer()
            const bytes = new Uint8Array(buffer)

            return await createChunkedAttachment(
                bytes,
                width,
                height,
                compressed,
                channelId,
                spaceId,
                userId,
                thumbnail,
                setProgress,
                customCreateMediaStream,
                customSendMediaPayload,
            )
        },
        [createChunkedAttachment],
    )

    const uploadAttachment = useCallback(
        async (
            channelId: string,
            spaceId: string | undefined,
            file: File,
            setProgress: (progress: number) => void,
            setError: () => void,
        ) => {
            try {
                if (isImageMimeType(file.type)) {
                    return await uploadImageFile(channelId, spaceId, undefined, file, setProgress)
                } else {
                    return await uploadFile(channelId, spaceId, file, setProgress)
                }
            } catch (e) {
                setError()
            }
        },
        [uploadImageFile, uploadFile],
    )

    const uploadTownImageToStream = useCallback(
        async (
            spaceId: string,
            file: File,
            setProgress: (progress: number) => void,
        ): Promise<{ ok: boolean; invalidationId?: string }> => {
            if (!isImageMimeType(file.type)) {
                return { ok: false }
            }
            console.log('uploadTownImageToStream', { spaceId })

            // set progress to 1 to start the spinner
            setProgress(1)

            try {
                const mediaInfo = (await uploadImageFile(
                    undefined,
                    spaceId,
                    undefined,
                    file,
                    () => {}, // disabled. Control the spinner in uploadTownImageToStream()
                )) as ChunkedMediaAttachment

                // add a spaceImage event to the stream
                // will be encrypted by client.setSpaceImage()
                // no need for encryption here
                const chunkedMedia = create(ChunkedMediaSchema, {
                    info: mediaInfo.info,
                    streamId: mediaInfo.streamId,
                    encryption: {
                        case: 'aesgcm',
                        value: {
                            iv: mediaInfo.encryption.iv,
                            secretKey: mediaInfo.encryption.secretKey,
                        },
                    },
                })
                const result = await setSpaceImage(spaceId, chunkedMedia)
                console.log('setSpaceImage', {
                    spaceId,
                    mediaStreamId: mediaInfo.streamId,
                    result,
                })

                const { invalidationId } = await refreshSpaceCache(spaceId)
                // no issues uploading the image
                return { ok: true, invalidationId }
            } catch (e) {
                console.error('Error uploading image to stream', e)
                return { ok: false }
            } finally {
                // stop the spinner
                setProgress(0)
            }
        },
        [setSpaceImage, uploadImageFile],
    )

    const uploadUserProfileImageToStream = useCallback(
        async (
            userId: string,
            file: File,
            setProgress: (progress: number) => void,
            customSetUserProfileImageFn?: (
                chunkedMediaInfo: PlainMessage<ChunkedMedia>,
                userId?: string,
            ) => Promise<{ eventId: string }>,
            customCreateMediaStream?: typeof createMediaStream,
            customSendMediaPayload?: typeof sendMediaPayload,
        ): Promise<boolean> => {
            const setUserProfileImageFn = customSetUserProfileImageFn || setUserProfileImage
            if (!isImageMimeType(file.type)) {
                return false
            }

            setProgress(1)

            return await retryOperation(
                async () => {
                    try {
                        const mediaInfo = (await uploadImageFile(
                            undefined,
                            undefined,
                            userId,
                            file,
                            () => {}, // disabled. We're controlling the spinner in uploadUserProfileImageToStream
                            customCreateMediaStream,
                            customSendMediaPayload,
                        )) as ChunkedMediaAttachment

                        // Will be encrypted by client.setUserProfileImage()
                        const chunkedMedia = create(ChunkedMediaSchema, {
                            info: mediaInfo.info,
                            streamId: mediaInfo.streamId,
                            encryption: {
                                case: 'aesgcm',
                                value: {
                                    iv: mediaInfo.encryption.iv,
                                    secretKey: mediaInfo.encryption.secretKey,
                                },
                            },
                        })
                        await setUserProfileImageFn(chunkedMedia, userId)
                        const { invalidationId } = await refreshUserImageCache(userId)
                        console.log('refreshUserImageCache', { userId, invalidationId })
                        if (invalidationId) {
                            await waitForInvalidation(invalidationId)
                        }
                        return true
                    } catch (e) {
                        console.error('Error uploading image to stream:', e)
                        throw e // Re-throw to trigger retry
                    } finally {
                        setProgress(0)
                    }
                },
                {
                    maxRetries: 3,
                    onError: (error: unknown, retryCount: number) => {
                        console.warn(
                            `Profile photo upload failed. Retry attempt ${retryCount}/3:`,
                            error,
                        )
                    },
                    onRetry: (retryCount: number) => {
                        console.log(
                            `Retrying entire profile photo upload (attempt ${retryCount}/3)...`,
                        )
                    },
                },
            )
        },
        [setUserProfileImage, uploadImageFile],
    )

    return { uploadAttachment, uploadTownImageToStream, uploadUserProfileImageToStream }
}

async function imageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => {
            resolve({ width: image.width, height: image.height })
        }
        image.onerror = reject
        image.src = URL.createObjectURL(file)
    })
}
